import type { CourseChatMessage } from "services/courseChatService";

export function formatFileSize(bytes: number | null | undefined): string {
  if (bytes == null || bytes <= 0) return "";
  const units = ["B", "KB", "MB", "GB"];
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value % 1 === 0 ? value : value.toFixed(1)} ${units[unit]}`;
}

export function isPdfAttachment(message: CourseChatMessage): boolean {
  const mime = message.attachment?.mime?.toLowerCase() ?? "";
  const name = message.attachment?.name?.toLowerCase() ?? "";
  return mime === "application/pdf" || name.endsWith(".pdf");
}

export function fileExtensionLabel(message: CourseChatMessage): string {
  const name = message.attachment?.name ?? "";
  const dot = name.lastIndexOf(".");
  if (dot >= 0 && dot < name.length - 1) {
    return name.slice(dot + 1).toUpperCase();
  }
  const mime = message.attachment?.mime ?? "";
  const slash = mime.lastIndexOf("/");
  return slash >= 0 ? mime.slice(slash + 1).toUpperCase() : "";
}

function sortKey(message: CourseChatMessage): number {
  return message.created_at_millis ?? message.local_created_at_millis ?? 0;
}

/**
 * Merge realtime Firestore messages with local optimistic (pending) messages.
 *  - drop pending messages once a Firestore message with the same client id or id arrives
 *  - never duplicate by id
 *  - failed pending messages remain (caller decides retry/delete)
 */
export function mergeChatMessages(
  real: CourseChatMessage[],
  pending: CourseChatMessage[]
): CourseChatMessage[] {
  const realClientIds = new Set(
    real.map((m) => m.client_message_id).filter((id): id is string => Boolean(id))
  );
  const realIds = new Set(real.map((m) => m.id));

  const livePending = pending.filter(
    (p) =>
      !realIds.has(p.id) &&
      (p.status === "failed" || !p.client_message_id || !realClientIds.has(p.client_message_id))
  );

  return [...real, ...livePending].sort((a, b) => sortKey(a) - sortKey(b));
}

export function makeClientMessageId(): string {
  const rand = Math.random().toString(36).slice(2, 8);
  return `local_${Date.now()}_${rand}`;
}
